import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import { blogPosts } from '../data/mockData';

const allCategories = ['All', 'Design Trends', 'Branding', 'Sustainability', 'Typography', 'Process'];

const extendedPosts = [
  ...blogPosts,
  {
    id: 4,
    title: "Typography as Brand Architecture",
    excerpt: "How typeface selection and typographic hierarchy can define your entire brand personality and customer perception.",
    image: "https://images.unsplash.com/photo-1765383563504-63f95cab942c?w=600&h=400&fit=crop",
    date: "Nov 22, 2024",
    category: "Typography",
    readTime: "7 min read"
  },
  {
    id: 5,
    title: "Behind the Process: A Brand Redesign",
    excerpt: "An inside look at our workflow when tackling a complete brand overhaul, from discovery to final delivery.",
    image: "https://images.unsplash.com/photo-1730206562928-0efd62560435?w=600&h=400&fit=crop",
    date: "Nov 5, 2024",
    category: "Process",
    readTime: "8 min read"
  },
  {
    id: 6,
    title: "Color Psychology in Modern Branding",
    excerpt: "Understanding how color choices influence customer emotions and behavior in today's saturated market.",
    image: "https://images.unsplash.com/photo-1619209629065-e9a2b225b24b?w=600&h=400&fit=crop",
    date: "Oct 18, 2024",
    category: "Branding",
    readTime: "5 min read"
  }
];

const Blog = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = extendedPosts.filter((post) => {
    const matchesCategory = activeFilter === 'All' || post.category === activeFilter;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase())
      || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <main className="pt-24 pb-0">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-16">
        <ScrollReveal>
          <span className="text-[#5f6368] text-xs tracking-wide uppercase">Blog</span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl text-[#1a1a1a] mt-4 tracking-tight max-w-3xl"
            style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
          >
            Insights & ideas from the studio
          </h1>
          <p className="text-base text-[#5f6368] mt-6 max-w-2xl leading-relaxed">
            Thoughts on design, branding, and the creative process. Lessons learned from years of crafting memorable brand experiences.
          </p>
        </ScrollReveal>
      </section>

      {/* Search + Filters */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-12">
        <ScrollReveal delay={100}>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              {allCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveFilter(cat)}
                  className={`px-4 py-2 rounded-full text-sm transition-all duration-200 ${
                    activeFilter === cat
                      ? 'bg-[#1a1a1a] text-white'
                      : 'bg-[#f8f9fa] text-[#5f6368] hover:bg-[#e8eaed]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5f6368]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-9 pr-4 py-2 rounded-full border border-[#e8eaed] text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors w-60"
              />
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Featured Post */}
      {filtered.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-16">
          <ScrollReveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 rounded-2xl overflow-hidden border border-[#e8eaed] bg-white">
              <div className="aspect-[16/11] lg:aspect-auto overflow-hidden">
                <img
                  src={filtered[0].image}
                  alt={filtered[0].title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-medium bg-[#f8f9fa] text-[#5f6368] px-3 py-1 rounded-full">
                    {filtered[0].category}
                  </span>
                  <span className="text-xs text-[#5f6368]">{filtered[0].date}</span>
                </div>
                <h2
                  className="text-2xl md:text-3xl text-[#1a1a1a] mb-4 tracking-tight"
                  style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
                >
                  {filtered[0].title}
                </h2>
                <p className="text-base text-[#5f6368] leading-relaxed mb-6">{filtered[0].excerpt}</p>
                <Link to={`/blog/${filtered[0].id}`} className="flex items-center gap-1 text-sm text-[#1a1a1a] font-medium cursor-pointer hover:gap-2 transition-all">
                  Read article <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </section>
      )}

      {/* Post Grid - Masonry Layout */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {filtered.slice(1).map((post, i) => (
            <div key={post.id} className="break-inside-avoid">
              <ScrollReveal delay={i * 50}>
                <Link to={`/blog/${post.id}`} className="group block cursor-pointer">
                  <div className="rounded-2xl overflow-hidden border border-[#e8eaed] bg-white hover:shadow-lg transition-shadow duration-300">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs text-[#5f6368]">{post.date}</span>
                        <span className="text-xs text-[#5f6368]">·</span>
                        <span className="text-xs text-[#5f6368]">{post.category}</span>
                      </div>
                      <h3 className="font-medium text-[#1a1a1a] mb-2 group-hover:text-[#5f6368] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-sm text-[#5f6368] leading-relaxed mb-4">{post.excerpt}</p>
                      <span className="text-xs text-[#5f6368]">{post.readTime}</span>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-[#5f6368]">No articles found matching your search.</p>
          </div>
        )}
      </section>
    </main>
  );
};

export default Blog;
